import { AllVisitsPageNS } from '@/pages/AllVisitsPage/AllVisitsPage.types';
import { VisitRequestWorkflowStep, VisitState } from '@/types/graphql';

export const getDefaultSelectedSecondaryTabs = (
    lowerTabs?: Set<AllVisitsPageNS.SecondaryTabConfigSet> | null,
): VisitRequestWorkflowStep[] => {
    const tabArray = lowerTabs ? Array.from(lowerTabs) : [];
    return tabArray.filter(([, isDefaultSelected]) => isDefaultSelected).map(([title]) => title);
};

export const parseSecondaryTabsFromUrl = (urlParam: string | null): VisitRequestWorkflowStep[] => {
    if (!urlParam) {
        return [];
    }

    return urlParam.split(',').map(decodeURIComponent) as VisitRequestWorkflowStep[];
};

export const isVisitState = (
    primaryTab: AllVisitsPageNS.PrimaryTab,
): primaryTab is Exclude<AllVisitsPageNS.PrimaryTab, 'NEEDS_ASSIGNMENT' | 'NEEDS_ATTENTION'> =>
    Object.values(VisitState).includes(primaryTab as VisitState);

export const isVisitRequest = (
    item?: AllVisitsPageNS.TableRow,
): item is AllVisitsPageNS.VisitRequestCell => item?.__typename === 'VisitRequest';

export const isNeedsAttentionVisitRequest = (
    item?: AllVisitsPageNS.TableRow,
): item is AllVisitsPageNS.AllNeedingAttentionVisitRequests[number] =>
    item?.__typename === 'VisitRequest' &&
    typeof (item as AllVisitsPageNS.AllNeedingAttentionVisitRequests[number]).warnings !==
        'undefined';

export const isNeedsAttentionVisit = (
    item?: AllVisitsPageNS.TableRow,
): item is AllVisitsPageNS.AllNeedingAttentionVisits[number] =>
    item?.__typename === 'Visit' &&
    typeof (item as AllVisitsPageNS.AllNeedingAttentionVisits[number]).warnings !== 'undefined';

export const isAssignmentVisitRequest = (
    item?: AllVisitsPageNS.TableRow,
): item is AllVisitsPageNS.AllNeedingAssignmentItems[number] =>
    item?.__typename === 'VisitRequest' &&
    typeof (item as AllVisitsPageNS.AllNeedingAttentionVisitRequests[number]).warnings ===
        'undefined';

export const isStateVisit = (
    item?: AllVisitsPageNS.TableRow,
): item is AllVisitsPageNS.AllVisitsByStateItems[number] =>
    item?.__typename === 'Visit' &&
    typeof (item as AllVisitsPageNS.AllNeedingAttentionVisits[number]).warnings === 'undefined';

export const isVisit = (item?: AllVisitsPageNS.TableRow): item is AllVisitsPageNS.VisitCell =>
    item?.__typename === 'Visit';

export const getDate = (item: AllVisitsPageNS.AllNeedingAttentionItems[number]) => {
    if (isVisit(item)) {
        return item.effectiveDate ?? null;
    }
    if (isVisitRequest(item)) {
        return item.tentativeDate ?? item.fulfillmentWindow?.startDate ?? null;
    }

    return null;
};

export const titleToWorkflowStep = (title: string): VisitRequestWorkflowStep | null => {
    const entry = Object.entries(AllVisitsPageNS.WorkflowStepTitles).find(([, t]) => t === title);
    return entry ? (entry[0] as VisitRequestWorkflowStep) : null;
};

export const titlesToWorkflowSteps = (titles: string[]): VisitRequestWorkflowStep[] => {
    return titles
        .map(titleToWorkflowStep)
        .filter((step): step is VisitRequestWorkflowStep => step !== null);
};

export const isWorkflowStep = (tab: unknown): tab is VisitRequestWorkflowStep =>
    typeof tab === 'string' &&
    Object.values(VisitRequestWorkflowStep).includes(tab as VisitRequestWorkflowStep);
